import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleCheck } from 'lucide-react';
import type { TwoFactorSetupDto } from '@aiai/shared';
import { enable2fa, setup2fa } from '../api';
import { errorMessage } from '../lib/api';
import { Callout } from './Callout';
import { Spinner } from './Spinner';

const STATUS_KEY = ['2fa-status'];

/**
 * TOTP enrollment flow (Begin setup → scan QR / confirm code → save recovery
 * codes). Shared by the Settings Security section and the mandatory
 * EnrollTwoFactorPage. `onEnrolled` fires once the user acknowledges their
 * recovery codes — Settings omits it (stays put), the enrollment page uses it
 * to continue into the app.
 */
export function TwoFactorEnroll({
  required,
  onEnrolled,
}: {
  required: boolean;
  onEnrolled?: () => void;
}) {
  const queryClient = useQueryClient();
  const [setup, setSetup] = useState<TwoFactorSetupDto | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const begin = useMutation({
    mutationFn: setup2fa,
    onSuccess: (data) => setSetup(data),
  });

  const confirm = useMutation({
    mutationFn: () => enable2fa({ code: code.trim() }),
    onSuccess: (data) => setRecoveryCodes(data.recoveryCodes),
  });

  const finish = () => {
    setRecoveryCodes(null);
    setSetup(null);
    setCode('');
    queryClient.invalidateQueries({ queryKey: STATUS_KEY });
    onEnrolled?.();
  };

  if (recoveryCodes) {
    return (
      <div className="card space-y-4 p-5">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-success-300">
          <CircleCheck className="h-4 w-4" aria-hidden="true" />
          Two-factor enabled
        </h3>
        <p className="text-sm text-ink-muted">
          Save these recovery codes somewhere safe. Each can be used once if you
          lose access to your authenticator. They won't be shown again.
        </p>
        <ul className="grid grid-cols-2 gap-2 rounded-lg bg-surface-2 p-4 font-mono text-sm text-ink">
          {recoveryCodes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <button type="button" className="btn-primary" onClick={finish}>
          I've saved my recovery codes
        </button>
      </div>
    );
  }

  return (
    <div className="card space-y-4 p-5">
      {required && (
        <Callout tone="warning">
          This deployment requires two-factor authentication. Set it up to
          continue.
        </Callout>
      )}
      <h3 className="text-sm font-semibold text-ink">
        Set up two-factor authentication
      </h3>

      {!setup ? (
        <>
          <p className="text-sm text-ink-muted">
            You'll scan a QR code with an authenticator app (Google
            Authenticator, 1Password, Authy, …) and confirm a code.
          </p>
          {begin.isError && (
            <Callout tone="danger">
              {errorMessage(begin.error, 'Could not start setup')}
            </Callout>
          )}
          <button
            type="button"
            className="btn-primary"
            onClick={() => begin.mutate()}
            disabled={begin.isPending}
          >
            {begin.isPending && <Spinner />}
            Begin setup
          </button>
        </>
      ) : (
        <ConfirmEnroll
          setup={setup}
          code={code}
          setCode={setCode}
          pending={confirm.isPending}
          error={
            confirm.isError
              ? errorMessage(confirm.error, 'That code was not accepted')
              : null
          }
          onConfirm={() => confirm.mutate()}
        />
      )}
    </div>
  );
}

function ConfirmEnroll({
  setup,
  code,
  setCode,
  pending,
  error,
  onConfirm,
}: {
  setup: TwoFactorSetupDto;
  code: string;
  setCode: (v: string) => void;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
}) {
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    onConfirm();
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-col items-start gap-4 sm:flex-row">
        <img
          src={setup.qrDataUrl}
          alt="Authenticator QR code"
          className="h-40 w-40 rounded-lg bg-white p-2"
        />
        <div className="text-sm text-ink-muted">
          <p>Scan the QR, or enter this secret manually:</p>
          <code className="mt-1 block break-all rounded bg-surface-2 px-2 py-1 font-mono text-xs text-ink">
            {setup.secret}
          </code>
        </div>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="label" htmlFor="enrollcode">
            Enter the current 6-digit code
          </label>
          <input
            id="enrollcode"
            name="otp"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            aria-label="One-time authentication code"
            className="input max-w-[12rem] text-center font-mono text-lg tracking-[0.3em]"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            required
            autoFocus
          />
        </div>
        {error && <Callout tone="danger">{error}</Callout>}
        <button type="submit" className="btn-primary" disabled={pending}>
          {pending && <Spinner />}
          Enable two-factor
        </button>
      </form>
    </div>
  );
}
